import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Dashboard from "./pages/Dashboard";
import CreateCard from "./pages/CreateCard";
import CreateProductPool from "./pages/CreateProductPool";
import CardView from "./pages/CardView";
import CardManage from "./pages/CardManage";
import BrowseCards from "./pages/BrowseCards";
import NotFound from "./pages/NotFound";
import Unsubscribe from "./pages/Unsubscribe";
import Profile from "./pages/Profile";

import Wishlists from "./pages/Wishlists";
import WishlistDetailPage from "./pages/WishlistDetail";
import GiftMe from "./pages/GiftMe";
import Admin from "./pages/Admin";
import ResetPassword from "./pages/ResetPassword";
import TemplateCheckout from "./pages/TemplateCheckout";
import { AssistantBubble } from "./components/assistant/AssistantBubble";

// React Query defaults tuned for the admin dashboard: keep data fresh
// for 30s (so flipping between admin tabs is instant) and retain cached
// data in memory for 5min so back/forward navigation skips refetches.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const DirectionManager = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const dir = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return <>{children}</>;
};

const App = () => (
  <ErrorBoundary>
    <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DirectionManager>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/dashboard" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                <Route path="/create" element={<ErrorBoundary><CreateCard /></ErrorBoundary>} />
                <Route path="/create/product" element={<ErrorBoundary><CreateProductPool /></ErrorBoundary>} />
                <Route path="/card/:slug" element={<ErrorBoundary><CardView /></ErrorBoundary>} />
                <Route path="/card/:slug/manage" element={<ErrorBoundary><CardManage /></ErrorBoundary>} />
                <Route path="/card/:slug/checkout" element={<ErrorBoundary><TemplateCheckout /></ErrorBoundary>} />
                <Route path="/cards/:occasion" element={<ErrorBoundary><BrowseCards /></ErrorBoundary>} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/profile" element={<ErrorBoundary><Profile /></ErrorBoundary>} />
                <Route path="/gift-preferences" element={<Navigate to="/profile?tab=gift-preferences" replace />} />
                <Route path="/wishlists" element={<ErrorBoundary><Wishlists /></ErrorBoundary>} />
                <Route path="/wishlists/:id" element={<ErrorBoundary><WishlistDetailPage /></ErrorBoundary>} />
                <Route path="/gift-me" element={<ErrorBoundary><GiftMe /></ErrorBoundary>} />
                <Route path="/admin" element={<ErrorBoundary><Admin /></ErrorBoundary>} />
                <Route path="/reset-password" element={<ResetPassword />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              {/* The assistant bubble calls useNavigate() internally, so it
                  has to live inside <BrowserRouter>. Rendering it after the
                  closing tag crashes the whole app with "useNavigate() may
                  be used only in the context of a <Router> component" when
                  the chat panel mounts. */}
              <AssistantBubble />
            </BrowserRouter>
          </TooltipProvider>
        </DirectionManager>
      </AuthProvider>
    </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
